import styles from 'css/src/components/common/ui/template-preview-button.module.scss';

import React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import classNames from 'classnames';

import type { Template } from 'js/src/client';
import { AspectRatio, TemplateStatusEnum } from 'js/src/client';
import { Toast } from 'js/src/components/common/Toast';
import { BUTTON_SIZES, SecondaryButton } from 'js/src/components/common/ui/Button';
import Video from 'js/src/components/common/Video';
import { useTemplateGallerySettings } from 'js/src/components/app/template-preview/TemplateGalleryContext';
import {
    areTemplatesLoading,
    getTemplateById,
    getTemplateDefaultAspectRatio,
    getTemplateDisplayName,
    getTemplateImageUrl,
    getTemplateMp4Url,
    getTemplateStatus,
    loadTemplateAndSceneDesigns,
} from 'js/src/data/templates';
import type { ImmutableMap } from 'js/src/libs/consts';
import type { RootState } from 'js/src/libs/RootState';

type PropsFromRedux = ConnectedProps<typeof connector>;

// The tile is sized by padding-top on a zero-height box, so these are
// height-over-width, and the ratio alone — any allowance for the name row is
// added on top.
const PREVIEW_ASPECT_RATIO_PADDING: Record<AspectRatio, string> = {
    [AspectRatio.LANDSCAPE]: '56.25%',
    [AspectRatio.SQUARE]: '100%',
    [AspectRatio.VERTICAL]: '177.78%',
    [AspectRatio.FOURFIVE]: '125%',
};

// The name row the shared layout puts under the preview. Overlaying the caption
// takes it out of flow, so only a tile that captions below needs the room.
const CAPTION_ROW_HEIGHT = '33px';

type OwnProps = {
    onClick?: (template: ImmutableMap<Template>) => void;
    selected?: boolean;
    hidePreviewButton?: boolean;
    templateId: string;
    forceSquareContain?: boolean;
    /**
     * Pins the tile to one aspect ratio instead of the template's own default,
     * so a row of previews reads at a single size however the templates in it
     * are shaped. The matching mp4 cut is used when the template has one.
     */
    previewAspectRatio?: AspectRatio;
    /**
     * The caller has taken the name out of flow — floated it over the preview
     * rather than leaving it under one — so the box is sized to the ratio alone,
     * with no room set aside for a caption row.
     */
    captionOverlaid?: boolean;
};

const TemplatePreviewButtonPresentation = React.memo(function TemplatePreviewButtonPresentation({
    onClick,
    selected,
    hidePreviewButton,
    templateId,
    loading,
    imageUrl,
    mp4Url,
    status,
    displayName,
    template,
    defaultAspectRatio,
    loadTemplate,
    forceSquareContain,
    previewAspectRatio,
    captionOverlaid,
}: PropsFromRedux & OwnProps) {
    /*
     This component renders a square block button which consists of an image.  On hover the background video plays on
     loop, previewing the theme/text transition/scene transition.  The component is clickable.
    */
    const { getTemplateHref } = useTemplateGallerySettings();
    const href = getTemplateHref?.(templateId);

    const [isHovering, setIsHovering] = React.useState(false);
    // Set when the user clicks before the template has loaded, so the pick can
    // be applied as soon as it arrives.
    const [pendingClick, setPendingClick] = React.useState(false);
    const hoverTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    const onMouseEnter = React.useCallback(() => {
        setIsHovering(true);
        // Prefetch template data after hovering for a bit to avoid unnecessary loads
        hoverTimeoutRef.current = setTimeout(() => {
            loadTemplate(templateId);
        }, 200);
    }, [loadTemplate, templateId]);

    const onMouseLeave = React.useCallback(() => {
        setIsHovering(false);
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
            hoverTimeoutRef.current = null;
        }
    }, []);

    React.useEffect(() => {
        return () => {
            if (hoverTimeoutRef.current) {
                clearTimeout(hoverTimeoutRef.current);
            }
        };
    }, []);

    const aspectRatioPadding = React.useMemo(() => {
        // A pinned ratio wins over the template's own, so a row of tiles reads at
        // one size; `forceSquareContain` squares the box and letterboxes inside it.
        const ratio = previewAspectRatio ?? (forceSquareContain ? AspectRatio.SQUARE : null);
        const padding =
            PREVIEW_ASPECT_RATIO_PADDING[ratio ?? defaultAspectRatio ?? AspectRatio.LANDSCAPE];
        // Sized to the ratio and no more when the caption floats over the preview.
        return captionOverlaid ? padding : `calc(${padding} + ${CAPTION_ROW_HEIGHT})`;
    }, [defaultAspectRatio, forceSquareContain, previewAspectRatio, captionOverlaid]);

    const onClickInternal = React.useCallback(
        (event?: React.MouseEvent) => {
            // The tile and the preview button inside it both handle clicks, so without this a
            // click on the button counts twice - two analytics events, two history entries.
            event?.stopPropagation();
            if (loading) {
                Toast.error('Please wait for the templates to load.');
                return;
            }
            // The template is normally prefetched on hover, so a click that lands
            // before that (or without any hover at all — touch, keyboard) would
            // otherwise be dropped. Fetch it now and complete the click once it
            // arrives, so a single click always works.
            if (!template) {
                setPendingClick(true);
                void loadTemplate(templateId);
                return;
            }
            if (onClick) {
                onClick(template);
            }
        },
        [loading, template, onClick, loadTemplate, templateId],
    );

    // Completes a click that arrived before the template had loaded.
    React.useEffect(() => {
        if (pendingClick && template && !loading) {
            setPendingClick(false);
            onClick?.(template);
        }
    }, [pendingClick, template, loading, onClick]);

    const onLinkClick = React.useCallback(
        (event: React.MouseEvent) => {
            // The link sits inside the tile, which opens the template on click too. Letting
            // this reach it would open the template twice over.
            event.stopPropagation();
            // Cmd/ctrl/shift/alt-click is the visitor asking the browser for the URL itself -
            // a new tab, a new window, a download. Handling it here as well would open the
            // template in the tab they meant to keep.
            if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
            event.preventDefault();
            onClickInternal();
        },
        [onClickInternal],
    );

    return (
        <div
            onClick={onClickInternal}
            className={classNames(styles['template-preview-button'], {
                [styles['selected']]: selected ?? false,
            })}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            data-testid="template-preview"
        >
            <div
                className={styles['preview-container']}
                data-testid="preview-container"
                style={{
                    paddingTop: aspectRatioPadding,
                }}
            >
                {(status === TemplateStatusEnum.DRAFT ||
                    status === TemplateStatusEnum.ARCHIVED) && (
                    <div className={styles['template-draft-status']}>{status}</div>
                )}
                <div
                    className={classNames(styles['preview'], {
                        [styles['preview--contain']]: forceSquareContain ?? false,
                    })}
                >
                    <Video preview_url={mp4Url} poster={imageUrl} play_on_hover={true} loop muted />
                    <div className="mx-l5-8 mt-l5-4">
                        <div className="d-flex justify-content-between align-items-center">
                            {href ? (
                                <a
                                    className={styles['template-name']}
                                    href={href}
                                    onClick={onLinkClick}
                                >
                                    {displayName}
                                </a>
                            ) : (
                                <div className={styles['template-name']}>{displayName}</div>
                            )}
                            {hidePreviewButton || loading ? null : (
                                <div
                                    className={classNames({
                                        invisible: !isHovering,
                                    })}
                                >
                                    <SecondaryButton
                                        data-testid="template-preview-button"
                                        onClick={onClickInternal}
                                        size={BUTTON_SIZES.SM}
                                        text="Preview"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

const mapStateToProps = (state: RootState, ownProps: OwnProps) => {
    let loading = false;
    let imageUrl: string | undefined;
    let mp4Url: string | undefined;
    let status = TemplateStatusEnum.ACTIVE;
    let displayName = '';
    let template: ImmutableMap<Template> | null = null;
    let defaultAspectRatio: AspectRatio | null = null;

    if (areTemplatesLoading(state)) {
        loading = true;
    } else {
        imageUrl = getTemplateImageUrl(state, ownProps.templateId) ?? undefined;
        mp4Url = getTemplateMp4Url(state, ownProps.templateId) ?? undefined;
        status = getTemplateStatus(state, ownProps.templateId) ?? TemplateStatusEnum.ACTIVE;
        displayName = getTemplateDisplayName(state, ownProps.templateId) ?? '';
        template = getTemplateById(state, ownProps.templateId) ?? null;

        // Use aspect-ratio-specific mp4 url if template has a default aspect ratio.
        // A pinned preview ratio wins over the template's own, so the cut that
        // plays matches the box it plays in.
        defaultAspectRatio = getTemplateDefaultAspectRatio(state, ownProps.templateId);
        const mp4AspectRatio = ownProps.previewAspectRatio ?? defaultAspectRatio;
        if (mp4AspectRatio) {
            const aspectRatioMp4Url = template?.get(
                `${mp4AspectRatio}_mp4_url` as keyof Template,
            ) as string | undefined;
            if (aspectRatioMp4Url) {
                mp4Url = aspectRatioMp4Url;
            }
        }
    }
    return {
        loading,
        imageUrl,
        mp4Url,
        status,
        displayName,
        template,
        defaultAspectRatio,
    };
};

const mapDispatchToProps = {
    loadTemplate: loadTemplateAndSceneDesigns,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

const TemplatePreviewButton = connector(TemplatePreviewButtonPresentation);

export { TemplatePreviewButtonPresentation };
export default TemplatePreviewButton;
